import { ThemeEvents } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

let activeGiftSyncController;
let lastOfferProgress;

const ARARA_TIER_TWO = 2;
const ARARA_TIER_BEST = 3;
let stopActiveDrawerRestore;

function keepCartDrawerOpenDuringUpdate(event, force = false) {
  const eventSource = event.detail?.data?.source;
  const targetIsCartItems = event.target instanceof Element && event.target.matches('cart-items-component');
  if (!force && eventSource !== 'cart-items-component' && !targetIsCartItems) return;

  const currentDrawer = document.querySelector('cart-drawer-component');
  const currentDialog = currentDrawer?.querySelector('dialog[ref="dialog"]');
  if (!currentDialog?.open || currentDialog.classList.contains('dialog-closing')) return;

  const currentContent = currentDialog.querySelector('.cart-drawer__content');
  const previousScrollTop = currentContent?.scrollTop || 0;
  stopActiveDrawerRestore?.();

  let stopped = false;
  let sawClosedDialog = false;
  let stableOpenChecks = 0;
  let drawerObserver;
  let restoreInterval;
  let restoreTimeout;

  const restoreOpenState = () => {
    if (stopped) return;

    const updatedDrawer = document.querySelector('cart-drawer-component');
    const updatedDialog = updatedDrawer?.querySelector('dialog[ref="dialog"]');

    if (updatedDialog?.classList.contains('dialog-closing')) {
      stopActiveDrawerRestore?.();
      return;
    }

    if (updatedDrawer && updatedDialog && !updatedDialog.open) {
      sawClosedDialog = true;
      stableOpenChecks = 0;

      try {
        updatedDialog.showModal();
      } catch (error) {
        const drawerTrigger = updatedDrawer.querySelector('[data-testid="cart-drawer-trigger"]');

        if (drawerTrigger instanceof HTMLButtonElement) {
          drawerTrigger.click();
        } else if (typeof updatedDrawer.open === 'function') {
          updatedDrawer.open();
        }
      }
    } else if (updatedDialog?.open && sawClosedDialog) {
      stableOpenChecks += 1;
    }

    const updatedContent = updatedDialog?.querySelector('.cart-drawer__content');
    if (updatedContent) updatedContent.scrollTop = previousScrollTop;

    if (sawClosedDialog && stableOpenChecks >= 6) stopActiveDrawerRestore?.();
  };

  stopActiveDrawerRestore = () => {
    stopped = true;
    drawerObserver?.disconnect();
    window.clearInterval(restoreInterval);
    window.clearTimeout(restoreTimeout);
  };

  drawerObserver = new MutationObserver(restoreOpenState);
  drawerObserver.observe(currentDrawer, {
    attributes: true,
    attributeFilter: ['open'],
    childList: true,
    subtree: true,
  });

  restoreInterval = window.setInterval(restoreOpenState, 50);
  restoreTimeout = window.setTimeout(stopActiveDrawerRestore, 2000);
  requestAnimationFrame(restoreOpenState);
}

function handleCartDrawerClick(event) {
  if (!(event.target instanceof Element)) return;

  if (event.target.closest('cart-drawer-component .cart-drawer__close-button')) {
    stopActiveDrawerRestore?.();
    return;
  }

  const removeButton = event.target.closest('.cart-items__remove');
  if (!removeButton?.closest('cart-items-component[data-drawer]')) return;

  keepCartDrawerOpenDuringUpdate(event, true);
}

function stopCartDrawerRestoreOnClose(event) {
  if (event.target instanceof Element && event.target.closest('cart-drawer-component')) {
    stopActiveDrawerRestore?.();
  }
}

window.addEventListener(ThemeEvents.cartUpdate, keepCartDrawerOpenDuringUpdate, true);
window.addEventListener('click', handleCartDrawerClick, true);
window.addEventListener(DialogCloseEvent.eventName, stopCartDrawerRestoreOnClose, true);
window.addEventListener('cancel', stopCartDrawerRestoreOnClose, true);

function getAraraOfferState(itemCount) {
  const count = Math.max(0, Number.isFinite(Number(itemCount)) ? Math.trunc(Number(itemCount)) : 0);

  if (count === 0) {
    return {
      primary: 'Buy 2, save 10% · Buy 3+, save 20%',
      secondary: '+ Complimentary Premium Gift Packaging at 3+',
      tier: 'discovery',
    };
  }

  if (count < ARARA_TIER_TWO) {
    return {
      primary: 'Add 1 more piece to unlock 10% OFF',
      secondary: '3+ pieces unlock 20% OFF',
      tier: 'one',
    };
  }

  if (count < ARARA_TIER_BEST) {
    return {
      primary: '10% OFF unlocked',
      secondary: 'Add 1 more piece for 20% OFF',
      tier: 'two',
    };
  }

  return {
    primary: '20% OFF unlocked',
    secondary: '+ Complimentary Premium Gift Packaging',
    tier: 'best',
  };
}

class AraraCartOffer extends HTMLElement {
  static observedAttributes = ['data-progress'];
  connectedCallback() {
    this.animateProgress();
    this.syncGiftPackaging();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-progress' || !this.isConnected || oldValue === null || oldValue === newValue) return;

    this.animateProgress(Number(oldValue));
  }

  animateProgress(previousProgress = lastOfferProgress) {
    const fill = this.querySelector('.arara-offer__fill');
    const targetProgress = Number(this.dataset.progress);

    if (!fill || !Number.isFinite(targetProgress)) return;

    const startingProgress = Number.isFinite(previousProgress) ? previousProgress : targetProgress;
    lastOfferProgress = targetProgress;

    if (startingProgress === targetProgress) return;

    fill.style.transition = 'none';

    requestAnimationFrame(() => {
      fill.style.width = `${startingProgress}%`;
      fill.getBoundingClientRect();

      requestAnimationFrame(() => {
        fill.style.transition = '';
        fill.style.width = `${targetProgress}%`;
      });
    });
  }

  async syncGiftPackaging() {
    const currentGift = this.dataset.currentGift || '';
    const desiredGift = this.dataset.desiredGift || '';

    if (currentGift === desiredGift) return;

    activeGiftSyncController?.abort();
    activeGiftSyncController = new AbortController();
    this.dataset.giftSync = 'pending';

    const baseRoute = typeof Theme !== 'undefined' ? Theme.routes.cart_update_url : '/cart/update';
    const updateRoute = baseRoute.endsWith('.js') ? baseRoute : `${baseRoute}.js`;

    try {
      const response = await fetch(updateRoute, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            'Premium Gift Packaging': desiredGift,
          },
        }),
        signal: activeGiftSyncController.signal,
      });

      if (!response.ok) throw new Error(`Gift packaging sync failed (${response.status})`);

      this.dataset.currentGift = desiredGift;
      this.dataset.giftSync = 'complete';
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.dataset.giftSync = 'error';
      console.warn('[ARARA] Premium Gift Packaging could not be synchronized.', error);
    }
  }
}

class AraraProductOffer extends HTMLElement {
  connectedCallback() {
    this.eventController?.abort();
    this.eventController = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.eventController.signal,
    });
    this.render(Number(this.dataset.itemCount));
  }

  disconnectedCallback() {
    this.eventController?.abort();
    this.cartRequestController?.abort();
  }

  handleCartUpdate = async (event) => {
    if (event.detail?.data?.didError) return;

    const eventItemCount = Number(event.detail?.resource?.item_count);
    if (Number.isFinite(eventItemCount)) {
      this.render(eventItemCount);
      return;
    }

    await this.refreshFromCart();
  };

  async refreshFromCart() {
    this.cartRequestController?.abort();
    this.cartRequestController = new AbortController();
    const cartRoute = typeof Theme !== 'undefined' ? Theme.routes.cart_url : '/cart';
    const cartJsonRoute = cartRoute.endsWith('.js') ? cartRoute : `${cartRoute}.js`;

    try {
      const response = await fetch(cartJsonRoute, {
        headers: { Accept: 'application/json' },
        signal: this.cartRequestController.signal,
      });

      if (!response.ok) throw new Error(`Cart state request failed (${response.status})`);

      const cart = await response.json();
      this.render(Number(cart.item_count));
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.warn('[ARARA] Product offer state could not be refreshed.', error);
    }
  }

  render(itemCount) {
    const primary = this.querySelector('[data-arara-offer-primary]');
    const secondary = this.querySelector('[data-arara-offer-secondary]');
    if (!primary || !secondary) return;

    const state = getAraraOfferState(itemCount);
    primary.textContent = state.primary;
    secondary.textContent = state.secondary;
    this.dataset.itemCount = String(Math.max(0, Math.trunc(Number(itemCount) || 0)));
    this.dataset.offerTier = state.tier;
  }
}

if (!customElements.get('arara-cart-offer')) {
  customElements.define('arara-cart-offer', AraraCartOffer);
}

if (!customElements.get('arara-product-offer')) {
  customElements.define('arara-product-offer', AraraProductOffer);
}
